import React, { useEffect, useState, useCallback, useRef } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import { CurrencyList, ContractPosition } from '../../../types'

import { Okei } from '../../../../Plans/Positions/Edit/types'

import {
    PerformanceContractDocPositionDraft,
    PerformanceContractDocPositionEdit,
} from '../types'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import PerformanceDocsEditPopupTableRow from './PerformanceInfoTable/PerformanceDocsEditPopupTableRow'
import PerformanceDocsAddFromContractPopup from './PerformanceDocsAddFromContractPopup'

const capitalizeFirstChar = (str: string) => {
    if (str.length === 0) {
        return '-' // Возвращаем пустую строку, если входная строка пуста
    }
    return str.charAt(0).toUpperCase() + str.slice(1)
}

const PERFORMANCE_CONTRACT_DOC_CODE: CodeItem[] = [
    { code: '01', name: 'товарная накладная' },
    { code: '02', name: 'товарно-транспортная накладная' },
    { code: '03', name: 'железнодорожная накладная' },
    { code: '04', name: 'авианакладная' },
    { code: '05', name: 'коносамент' },
    { code: '06', name: 'акт о приемке товаров' },
    {
        code: '07',
        name: 'акт о приемке товаров, поступившего без счета поставщика',
    },
    {
        code: '08',
        name: 'акт о приемке-передаче объекта основных средств (кроме зданий, сооружений)',
    },
    { code: '09', name: 'акт о приемке-передаче здания (сооружения)' },
    {
        code: '10',
        name: 'акт о приемке-передаче групп объектов основных средств (кроме зданий, сооружений)',
    },
    {
        code: '11',
        name: 'акт о приеме-сдаче отремонтированных, реконструированных модернизированных объектов основных средств',
    },
    { code: '12', name: 'акт о приемке выполненных работ' },
    { code: '13', name: 'акт приемке законченного строительством объекта' },
    { code: '18', name: 'счет на оплату' },
    { code: '19', name: 'счет-фактура' },
    { code: '20', name: 'акт выполненных работ' },
    { code: '21', name: 'акт о приемке материалов' },
    { code: '22', name: 'акт о завесе тары' },
    {
        code: '23',
        name: 'акт об оприходовании тары, не указанной в счете поставщика',
    },
    {
        code: '24',
        name: 'акт о выявлении недостатков выполненных работ (оказанных услуг)',
    },
    { code: '25', name: 'акт о невыполнении работ (оказании услуг)' },
    { code: '26', name: 'акт - рекламация' },
    { code: '31', name: 'акт о приеме (поступлении) оборудования' },
    { code: '37', name: 'прочие документы о приемке' },
]

type Props = {
    theme: string
    performanceGuid: string | undefined
    currencyList: CurrencyList[]
    handlePerformanceDocsEditPopup: (
        performance_contract_doc_guid: string
    ) => void
    performanceContractDocsPositionEdit:
        | PerformanceContractDocPositionEdit
        | null
        | undefined
    isLoadingPerformanceDocsPositionEdit: boolean
    onPrepayment: (doc_prepayment: boolean) => void
    onDocNumber: (doc_number: string) => void
    onDocDate: (doc_date: string) => void
    onDocCode: (doc_code: string) => void
    onDocName: (doc_name: string) => void
    onChangeExchangeRate: (exchange_rate: number) => void
    onChangeExchangeRateDate: (exchange_rate_date: string) => void
    onChangeCurrency: (currency: {
        code: string
        name: string
        digital_code: number
    }) => void
    onSaveDoc: (data: PerformanceContractDocPositionEdit | null) => void
    isSavingPerformanceDoc: boolean

    onUpdateItem: (
        ordinal: number,
        patch: Partial<
            PerformanceContractDocPositionEdit['performance_contract_doc_position_item'][number]
        >
    ) => void
    onRemoveItems: (ordinals: number[]) => void
    onAddItems: (rows: ContractPosition[]) => void
    contractGuid: string | undefined
    contractRegNumber: string | undefined
}

interface CodeItem {
    code: string
    name: string
}

const PerformanceDocsEditPopup: React.FC<Props> = (props) => {
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [okeiList, setOkeiList] = useState<Okei[]>([])

    const [
        performanceDocsAddFromContractPopup,
        setPerformanceDocsAddFromContractPopup,
    ] = useState<boolean>(false)

    const [
        deleteRowPerformanceDocsPositionPopup,
        setDeleteRowPerformanceDocsPositionPopup,
    ] = useState<boolean>(false)

    const getOkeiList = async (): Promise<Okei[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/okei', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на способы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (ordinal: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(ordinal)) {
                return prevSelectedItems.filter((itemId) => itemId !== ordinal)
            } else {
                return [...prevSelectedItems, ordinal]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (ordinal: number) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(ordinal)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, ordinal] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems = props.performanceContractDocsPositionEdit
                ? props.performanceContractDocsPositionEdit.performance_contract_doc_position_item.map(
                      (item) => item.ordinal
                  )
                : []

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    // безопасный парсер десятичных (точка)
    const parseDec = (v: string | number | null | undefined) => {
        if (v === null || v === undefined) return undefined
        if (typeof v === 'number') return v
        const num = Number(String(v).replace(',', '.'))
        return Number.isNaN(num) ? undefined : num
    }

    // patch одной позиции
    const handleItemPatch = useCallback(
        (
            ordinal: number,
            patch: Partial<
                PerformanceContractDocPositionDraft['performance_contract_doc_position_item'][number]
            >
        ) => {
            props.onUpdateItem(ordinal, patch)
        },
        [props]
    )

    const handleDeleteRowPerformanceDocsPositionPopup = () => {
        setDeleteRowPerformanceDocsPositionPopup(
            !deleteRowPerformanceDocsPositionPopup
        )
    }

    // удалить выбранные
    const handleDeleteSelected = useCallback(() => {
        if (!selectedItems.length) return
        props.onRemoveItems(selectedItems)
        setSelectedItems([])
        setSelectAll(false)
    }, [selectedItems, props])

    useEffect(() => {
        const fetchData = async () => {
            const okei = await getOkeiList()
            if (okei) {
                setOkeiList(okei)
            }
        }
        fetchData()
    }, [])

    const handlePerformanceDocsAddFromContractPopup = () => {
        setPerformanceDocsAddFromContractPopup(
            !performanceDocsAddFromContractPopup
        )
    }

    const draft = props.performanceContractDocsPositionEdit

    const hasDocNumber =
        typeof draft?.performance_contract_doc_number === 'string' &&
        draft.performance_contract_doc_number.trim().length > 0

    const hasDocDate =
        typeof draft?.performance_contract_doc_date === 'string' &&
        draft.performance_contract_doc_date.trim().length > 0

    // Можно проверять либо по коду, либо по имени – берем код
    const hasDocName =
        typeof draft?.performance_contract_doc_code === 'string' &&
        draft.performance_contract_doc_code.trim().length > 0

    const hasCurrency =
        draft?.currency_digital_code !== null &&
        draft?.currency_digital_code !== undefined &&
        draft?.currency_digital_code !== ('' as unknown)

    const isRub = draft?.currency_digital_code === 643

    const hasExchangeRateDate =
        typeof draft?.exchange_rate_date === 'string' &&
        draft.exchange_rate_date.trim().length > 0

    const hasExchangeRate =
        draft?.exchange_rate !== null &&
        draft?.exchange_rate !== undefined &&
        draft?.exchange_rate !== ('' as unknown)

    const hasContractDocsPositionDraftItem =
        props.performanceContractDocsPositionEdit
            ? props.performanceContractDocsPositionEdit
                  .performance_contract_doc_position_item?.length > 0
            : false

    const canEditDoc =
        hasDocNumber &&
        hasDocDate &&
        hasDocName &&
        hasCurrency &&
        (isRub || (hasExchangeRateDate && hasExchangeRate)) &&
        hasContractDocsPositionDraftItem

    const isEditDisabled = !canEditDoc || props.isSavingPerformanceDoc

    return (
        <div className="fixed z-10 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
            <div
                className="flex flex-col p-5 rounded-lg w-[1600px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                    Добавление документа
                </h2>

                <div className="flex flex-col w-full gap-3">
                    <div className="flex flex-col w-[200px]">
                        <Checkbox
                            label="Оплата авансом"
                            checked={
                                props.performanceContractDocsPositionEdit
                                    ? props.performanceContractDocsPositionEdit
                                          .prepayment
                                    : false
                            }
                            onChange={props.onPrepayment}
                            disabled={false}
                        />
                    </div>

                    <div className="flex flex-row w-full h-fit gap-3 items-center justify-start">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Номер документа
                            </label>
                            <input
                                type="text"
                                className={`form-input user-input-placeholder w-full`}
                                value={
                                    props.performanceContractDocsPositionEdit
                                        ?.performance_contract_doc_number !=
                                    null
                                        ? props
                                              .performanceContractDocsPositionEdit
                                              .performance_contract_doc_number
                                        : ''
                                }
                                onChange={(e) =>
                                    props.onDocNumber(e.target.value)
                                }
                            />
                        </div>
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Дата документа
                            </label>
                            <input
                                type="date"
                                style={{
                                    colorScheme:
                                        props.theme == 'dark'
                                            ? 'dark'
                                            : 'light',
                                }}
                                className="form-input w-full bg-[#1F1E1F]"
                                value={
                                    props.performanceContractDocsPositionEdit
                                        ?.performance_contract_doc_date != null
                                        ? props
                                              .performanceContractDocsPositionEdit
                                              .performance_contract_doc_date
                                        : ''
                                }
                                onChange={(e) =>
                                    props.onDocDate(e.target.value)
                                }
                            />
                        </div>
                        <div className="flex flex-col w-[800px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Наименование документа
                            </label>
                            <select
                                className="form-select"
                                value={
                                    props.performanceContractDocsPositionEdit
                                        ?.performance_contract_doc_code != null
                                        ? props
                                              .performanceContractDocsPositionEdit
                                              .performance_contract_doc_code
                                        : ''
                                }
                                onChange={(e) => {
                                    const selectedCode = e.target.value
                                    const selectedItem =
                                        PERFORMANCE_CONTRACT_DOC_CODE.find(
                                            (item) => item.code === selectedCode
                                        )

                                    props.onDocCode(selectedCode)
                                    if (selectedItem) {
                                        props.onDocName(selectedItem.name)
                                    }
                                }}
                            >
                                <option key={''} value={''}>
                                    Выбрать
                                </option>
                                {PERFORMANCE_CONTRACT_DOC_CODE.map(
                                    (item, index) => (
                                        <option key={index} value={item.code}>
                                            {item.name
                                                ? capitalizeFirstChar(item.name)
                                                : item.name}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-row w-full h-fit gap-3 items-center justify-start">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Валюта
                            </label>
                            <select
                                className="form-select px-2 rounded-md w-full h-[28px]"
                                value={
                                    props.performanceContractDocsPositionEdit
                                        ?.currency_digital_code != null
                                        ? props
                                              .performanceContractDocsPositionEdit
                                              .currency_digital_code
                                        : ''
                                }
                                onChange={(e) => {
                                    const selectedCode = e.target.value
                                    const selectedItem =
                                        props.currencyList.find(
                                            (item) =>
                                                item.num_code === selectedCode
                                        )
                                    if (selectedItem) {
                                        props.onChangeCurrency({
                                            code: selectedItem.char_code,
                                            name: selectedItem.name,
                                            digital_code: Number(
                                                selectedItem.num_code
                                            ),
                                        })
                                    }
                                }}
                            >
                                <option value="">Выбрать</option>
                                {props.currencyList.map((curr, index) => (
                                    <option key={index} value={curr.num_code}>
                                        {curr.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {props.performanceContractDocsPositionEdit
                            ?.currency_digital_code != null ? (
                            props.performanceContractDocsPositionEdit
                                .currency_digital_code != 643 ? (
                                <>
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Дата валюты
                                        </label>
                                        <input
                                            type="date"
                                            style={{
                                                colorScheme:
                                                    props.theme == 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                            }}
                                            className="form-input w-full bg-[#1F1E1F]"
                                            value={
                                                props
                                                    .performanceContractDocsPositionEdit
                                                    ?.exchange_rate_date != null
                                                    ? props
                                                          .performanceContractDocsPositionEdit
                                                          .exchange_rate_date
                                                    : ''
                                            }
                                            onChange={(e) => {
                                                const dotString =
                                                    e.target.value ?? ''
                                                dotString === ''
                                                    ? ''
                                                    : dotString
                                                props.onChangeExchangeRateDate(
                                                    e.target.value
                                                )
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Курс валюты
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-input user-input-placeholder w-full`}
                                            // placeholder="Введите номер документа"
                                            value={
                                                props
                                                    .performanceContractDocsPositionEdit
                                                    ?.exchange_rate != null
                                                    ? props
                                                          .performanceContractDocsPositionEdit
                                                          .exchange_rate
                                                    : ''
                                            }
                                            onChange={(e) => {
                                                const dotString =
                                                    e.target.value ?? ''
                                                dotString === ''
                                                    ? ''
                                                    : dotString
                                                props.onChangeExchangeRate(
                                                    Number(e.target.value)
                                                )
                                            }}
                                        />
                                    </div>
                                </>
                            ) : (
                                ''
                            )
                        ) : (
                            ''
                        )}
                    </div>
                </div>

                <div className="flex flex-col w-full h-fit items-center justify-center bg-[#e4e6e8] dark:bg-[#242225] rounded">
                    <div className="flex flex-row gap-1 p-1 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033] items-center justify-center">
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <div className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <div
                                    className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleSelectAllChange}
                                >
                                    <HoverTooltip tooltipText="Выбрать все">
                                        <input
                                            type="checkbox"
                                            className="p-2"
                                            checked={selectAll}
                                            onChange={handleSelectAllChange}
                                        />
                                    </HoverTooltip>
                                </div>
                            </div>
                        </div>
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <button
                                className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={
                                    handlePerformanceDocsAddFromContractPopup
                                }
                            >
                                <HoverTooltip tooltipText="Добавить позицию из договора">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 h-full items-center justify-center text-center">
                            <button
                                className={`w-6 h-6 grid rounded-full place-items-center ${selectedItems.length > 0 ? 'hover:bg-[#ed9cae] dark:hover:bg-[#701f1f]' : ''}`}
                                onClick={
                                    handleDeleteRowPerformanceDocsPositionPopup
                                }
                                disabled={!(selectedItems.length > 0)}
                            >
                                <HoverTooltip tooltipText="Удалить">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                selectedItems.length > 0
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex flex-1 w-full h-full items-center justify-center text-center"></div>
                    </div>
                    <div className="flex flex-row gap-1 w-full h-[40px] p-1 px-2 items-center justify-center text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-10 h-full items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex flex-1 items-center justify-center ">
                            Наименование
                        </div>
                        <div className="flex w-[165px] items-center justify-center text-center">
                            Страна происхождения товара
                        </div>
                        <div className="flex w-[165px] items-center justify-center text-center">
                            Страна регистрации производителя товара
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Количество (объем)
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Единица измерения
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Цена за единицу
                        </div>
                        <div className="flex w-32 items-center justify-center text-center">
                            Сумма по позиции
                        </div>
                    </div>
                    <div className="flex flex-col w-full h-[400px] gap-1 py-1 overflow-y-auto custom-scrollbar">
                        {props.isLoadingPerformanceDocsPositionEdit ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1 text-[11px] items-start justify-center">
                                {props.performanceContractDocsPositionEdit
                                    ? props.performanceContractDocsPositionEdit.performance_contract_doc_position_item.map(
                                          (row, index) => (
                                              <PerformanceDocsEditPopupTableRow
                                                  key={
                                                      row.contract_position_guid
                                                  }
                                                  performanceContractDocPositionItem={
                                                      row
                                                  }
                                                  exchangeRate={
                                                      props
                                                          .performanceContractDocsPositionEdit
                                                          ?.exchange_rate ??
                                                      null
                                                  }
                                                  currencyDigitalCode={
                                                      props
                                                          .performanceContractDocsPositionEdit
                                                          ?.currency_digital_code ??
                                                      643
                                                  }
                                                  currencyList={
                                                      props.currencyList
                                                  }
                                                  selected={selectedItems.includes(
                                                      row.ordinal
                                                  )}
                                                  theme={props.theme}
                                                  onClick={(ordinal) => {
                                                      handleCheckboxChange(
                                                          ordinal
                                                      )
                                                  }}
                                                  onRightClick={(ordinal) => {
                                                      handleRightClickCheckboxChange(
                                                          ordinal
                                                      )
                                                  }}
                                                  // NEW — редактирование полей строки:
                                                  onChangeQty={(
                                                      val: string
                                                  ) => {
                                                      const qty = parseDec(val)
                                                      if (qty !== undefined)
                                                          handleItemPatch(
                                                              row.ordinal,
                                                              { qty }
                                                          )
                                                  }}
                                                  onChangeUnitPrice={(
                                                      val: string
                                                  ) => {
                                                      const unit_price =
                                                          parseDec(val)
                                                      if (
                                                          unit_price !==
                                                          undefined
                                                      )
                                                          handleItemPatch(
                                                              row.ordinal,
                                                              { unit_price }
                                                          )
                                                  }}
                                                  onChangeRubUnitPrice={(
                                                      val: string
                                                  ) => {
                                                      const rub_unit_price =
                                                          parseDec(val)
                                                      if (
                                                          rub_unit_price !==
                                                          undefined
                                                      )
                                                          handleItemPatch(
                                                              row.ordinal,
                                                              { rub_unit_price }
                                                          )
                                                  }}
                                                  onChangeTotalRub={(
                                                      val: string
                                                  ) => {
                                                      const total_rub =
                                                          parseDec(val)
                                                      if (
                                                          total_rub !==
                                                          undefined
                                                      )
                                                          handleItemPatch(
                                                              row.ordinal,
                                                              { total_rub }
                                                          )
                                                  }}
                                                  onUpdateItem={
                                                      props.onUpdateItem
                                                  }
                                                  okeiList={okeiList}
                                              />
                                          )
                                      )
                                    : ''}
                            </div>
                        )}
                    </div>
                    {/* buttons */}
                    <div className="flex flex-row gap-1 w-full h-[40px] text-[10px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                        <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                            <button
                                className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                disabled={false}
                                onClick={
                                    handlePerformanceDocsAddFromContractPopup
                                }
                            >
                                <svg
                                    className="w-3 h-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 22 22"
                                    id="plus"
                                >
                                    <path
                                        fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                    ></path>
                                </svg>
                                Добавить
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ed9cae] dark:hover:bg-[#701f1f] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                disabled={!(selectedItems.length > 0)}
                                onClick={
                                    handleDeleteRowPerformanceDocsPositionPopup
                                }
                            >
                                <svg
                                    className="w-3 h-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 22 22"
                                    id="trash"
                                >
                                    <path
                                        fill={
                                            selectedItems.length > 0
                                                ? props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                                : props.theme == 'dark'
                                                  ? '#8A8A8A'
                                                  : '#6A6A6A'
                                        }
                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                    ></path>
                                </svg>
                                Удалить
                            </button>
                        </div>
                        <div className="flex flex-1 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        <div className="flex w-8 h-full items-center justify-center text-center"></div>
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={() => {
                            props.handlePerformanceDocsEditPopup(
                                props.performanceContractDocsPositionEdit
                                    ?.performance_contract_position_guid ?? ''
                            )
                        }}
                    >
                        Отмена
                    </button>
                    <button
                        className={`flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs ${
                            isEditDisabled
                                ? 'text-[#666666] dark:text-[#888888] bg-[#f6f6f6] dark:bg-[#494849]'
                                : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                        } `}
                        disabled={isEditDisabled}
                        onClick={() => {
                            if (isEditDisabled) return

                            props.onSaveDoc(
                                props.performanceContractDocsPositionEdit
                                    ? props.performanceContractDocsPositionEdit
                                    : null
                            )
                            props.handlePerformanceDocsEditPopup(
                                props.performanceContractDocsPositionEdit
                                    ?.performance_contract_position_guid ?? ''
                            )
                        }}
                    >
                        Сохранить
                    </button>
                </div>
            </div>
            {deleteRowPerformanceDocsPositionPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить позиции?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteRowPerformanceDocsPositionPopup
                                    }
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() => {
                                        handleDeleteSelected()
                                        handleDeleteRowPerformanceDocsPositionPopup()
                                    }}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {performanceDocsAddFromContractPopup && (
                <PerformanceDocsAddFromContractPopup
                    theme={props.theme}
                    currencyList={props.currencyList}
                    contractGuid={props.contractGuid}
                    contractRegNumber={props.contractRegNumber}
                    handlePerformanceDocsAddFromContractPopup={
                        handlePerformanceDocsAddFromContractPopup
                    }
                    onAddItems={props.onAddItems}
                />
            )}
        </div>
    )
}
export default PerformanceDocsEditPopup
